import { Link } from "react-router-dom";
import { FadeIn } from "../utils/FadeIn";
import { PromangLogo } from "../utils/PromangLogo";
import { usePublicJobOpenings } from "../../hooks/usePublicJobOpenings";
import { formatJobDate, jobOpeningPath } from "../../data/jobOpeningPublic";

const MAX_VISIBLE = 3;

function PromangSyncBadge({ loading }) {
  return (
    <div className="source-badge" aria-label="Synkroniseres fra Promang">
      <span className="pulse" />
      <span style={{ fontFamily: "var(--mono)", fontSize: 11 }}>
        {loading ? "Henter fra" : "Synk fra"}
      </span>
      <PromangLogo height={14} color="var(--airon-3)" />
    </div>
  );
}

/**
 * Homepage careers strip — same Airon language as FeaturedProjects.
 * Public openings from Promang; static vacancies if API is empty or down.
 */
export default function CareersSection() {
  // Same params as JobsNotice's usePublicJobOpenings call so the two
  // homepage sections share one deduped request instead of firing two.
  const { items, loading, totalCount } = usePublicJobOpenings({ per_page: 50 });
  const shown = items.slice(0, MAX_VISIBLE);

  if (!loading && shown.length === 0) return null;

  return (
    <section className="sec" id="karriere">
      <div className="wrap">
        <div className="sec-head">
          <div className="sec-tag">Karriere</div>
          <h2>
            Bli en del av <em>laget.</em>
          </h2>
          <PromangSyncBadge loading={loading} />
        </div>

        {loading ? (
          <div className="careers-grid" aria-hidden="true">
            {Array.from({ length: MAX_VISIBLE }).map((_, i) => (
              <div key={`skeleton-${i}`} className="career-card" style={{ opacity: 0.55 }}>
                <div
                  style={{
                    height: 12,
                    width: 72,
                    background: "var(--bg-3)",
                    borderRadius: 999,
                    marginBottom: 16,
                  }}
                />
                <div
                  style={{
                    height: 22,
                    width: "70%",
                    background: "var(--bg-3)",
                    borderRadius: 6,
                    marginBottom: 12,
                  }}
                />
                <div
                  style={{
                    height: 12,
                    width: "92%",
                    background: "var(--bg-2)",
                    borderRadius: 6,
                    marginBottom: 6,
                  }}
                />
                <div
                  style={{
                    height: 12,
                    width: "64%",
                    background: "var(--bg-2)",
                    borderRadius: 6,
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="careers-grid">
            {shown.map((job) => (
              <FadeIn key={job.id || job.slug}>
                <Link
                  to={job.path || jobOpeningPath(job)}
                  className="career-card"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div className="career-tags">
                    {(job.employment_type_label || job.employment_type) && (
                      <span className="career-chip">
                        {job.employment_type_label || job.employment_type}
                      </span>
                    )}
                    {job.location && (
                      <span className="career-chip muted">{job.location}</span>
                    )}
                  </div>
                  <h3 className="career-title">{job.title}</h3>
                  <p className="career-excerpt">
                    {job.short_description ||
                      "Se stillingsannonsen for mer informasjon."}
                  </p>
                  <div className="career-meta">
                    <span>
                      {job.application_deadline
                        ? `Frist ${formatJobDate(job.application_deadline)}`
                        : "Åpen søknad"}
                    </span>
                    <span className="career-more">Les mer →</span>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        )}

        <div
          style={{
            marginTop: 28,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <Link to="/stillinger" className="btn ghost">
            Se alle stillinger
            {totalCount > MAX_VISIBLE ? ` (${totalCount})` : ""}{" "}
            <span className="arr">→</span>
          </Link>

          <a
            href="https://promang.no"
            target="_blank"
            rel="noreferrer noopener"
            title="Powered by Promang"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              color: "var(--muted)",
              textDecoration: "none",
              fontFamily: "var(--mono)",
              fontSize: 11,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            <span>Powered by</span>
            <PromangLogo height={16} color="var(--ink-2)" />
          </a>
        </div>
      </div>
    </section>
  );
}
